/**
 * Per-customer rules for the quote email envelope.
 *
 * Every customer-specific decision the email preview makes - who may be CC'd,
 * whether the subject is kept verbatim, whether saved-remark files go out,
 * whether the Excel automation runs on open, which portal warning to show -
 * is decided here, from the RFQ and its subject, and nowhere else in the UI.
 *
 * The Seven Seas half mirrors backend `services/customers/sevenSeas.js`, which
 * enforces the same rules again at send time; `customerRules.test.ts` runs the
 * cases from `backend-node/src/tests/seven-seas-quotes.test.js` against this
 * module so the two cannot drift apart unnoticed.
 */

import type { RFQ } from '../types/rfq';

/** A procurement portal whose quoting procedure differs from a plain reply. */
type CustomerPortal = 'seven_seas' | 'archangel' | 'epbrowser';

export interface CustomerProfile {
  /** CC must stay empty - not even the usual dbrsystems copy. */
  ccLocked: boolean;
  /** The subject goes out byte-identical: no "Re:" prefix. */
  keepSubject: boolean;
  /** Files held by saved remarks are never attached. */
  excludeRemarkFiles: boolean;
  /** Run the Excel automation as soon as the dialog opens. */
  autoExcel: boolean;
  /**
   * An address to add to CC when the dialog opens: wrist.com's Excel contact,
   * or the ARC user behind an Archangel enquiry (the portal's Sender Email).
   */
  ccPrefill: string | null;
  /** The portal this RFQ came through, if any. */
  portal: CustomerPortal | null;
}

// The only sender of their portal RFQs; their staff also email DBR directly.
const SEVEN_SEAS_SUPPLIER_INBOX = 'supplier.sevenseas@sevenseasgroup.com';
// "SevenSeas REQUEST FOR QUOTE 7.14.2026.123456 - Supplier: XXXXX - User: ..."
const SEVEN_SEAS_SUBJECT_PREFIX = /^sevenseas\s+request\s+for\s+quote/i;
// Sinwa's epbrowser RFQs: "PRFQ_(TRFQ..)(DBR SYSTEMS PTE LTD)(TQ..)(<buyer>)"
const EPB_SUBJECT_PREFIX = /^PRFQ_\(/i;

/**
 * Does this RFQ belong to the Seven Seas portal? Same rule as the backend's
 * `isSevenSeasRfq`: their supplier inbox, or their RFQ subject with any
 * leading reply/forward prefixes stripped (a forwarded RFQ keeps it). Their
 * domain alone is not the portal: staff also email DBR directly (RFQ 63550),
 * and those replies go back to the person who asked.
 */
export function isSevenSeasRfq({
  clientEmail,
  subject,
}: { clientEmail?: string | null; subject?: string | null } = {}): boolean {
  const email = String(clientEmail || '').trim().toLowerCase();
  if (email === SEVEN_SEAS_SUPPLIER_INBOX) return true;
  const cleaned = String(subject || '')
    .trim()
    .replace(/^((re|fw|fwd)\s*:\s*)+/i, '');
  return SEVEN_SEAS_SUBJECT_PREFIX.test(cleaned);
}

/**
 * Archangel procurement portal (southnests): the RFQ email comes from the
 * portal's automated archangel.* mailbox and only carries a link; the items
 * were read from the portal (backend services/archangelPortal.js).
 */
function isArchangelPortalRfq(clientEmail: string | null | undefined): boolean {
  const localPart = (clientEmail || '').toLowerCase().split('@')[0];
  return localPart.startsWith('archangel');
}

/**
 * epbrowser procurement portal (Sinwa Global): the RFQ's client is the
 * purchaser named on it, not the noreply sender, so this keys on the subject.
 * Any number of reply/forward prefixes are stripped.
 */
function isEpbPortalRfq(subject: string | null | undefined): boolean {
  const cleaned = (subject || '').trim().replace(/^((re|fw|fwd)\s*:\s*)+/i, '');
  return EPB_SUBJECT_PREFIX.test(cleaned);
}

/**
 * The envelope rules for this RFQ.
 *
 * @param originalSubject the subject of the customer's RFQ email, as fetched
 *   for threading (null until it arrives).
 * @param currentSubject the subject currently in the compose field. Seven
 *   Seas detection falls back to it before the original subject arrives; the
 *   portal checks fall back to `rfq.original_subject` instead.
 */
export function customerProfile(
  rfq: Pick<RFQ, 'client_email' | 'original_subject' | 'excel_contact_email'>,
  originalSubject: string | null | undefined,
  currentSubject?: string | null,
): CustomerProfile {
  const sevenSeas = isSevenSeasRfq({
    clientEmail: rfq.client_email,
    subject: originalSubject || currentSubject,
  });
  const wrist = Boolean(rfq.client_email?.toLowerCase().endsWith('wrist.com'));

  // The three portals are told apart by disjoint senders and subject
  // prefixes; the order only matters for an RFQ that matched more than one.
  let portal: CustomerPortal | null = null;
  if (sevenSeas) portal = 'seven_seas';
  else if (isArchangelPortalRfq(rfq.client_email)) portal = 'archangel';
  else if (isEpbPortalRfq(originalSubject || rfq.original_subject)) portal = 'epbrowser';

  // The person behind an automated mailbox: Wrist's contact from their Excel,
  // or the ARC user who raised an Archangel enquiry (the portal's sender
  // email, stored at ingestion - ARC asked for them on CC).
  const ccsContact = wrist || portal === 'archangel';

  return {
    ccLocked: sevenSeas,
    keepSubject: sevenSeas,
    excludeRemarkFiles: sevenSeas,
    autoExcel: wrist || sevenSeas,
    ccPrefill: ccsContact && rfq.excel_contact_email ? rfq.excel_contact_email : null,
    portal,
  };
}
